/**
 * useInboxState — core state hook for the inbox email client.
 *
 * Fetches emails from the API via fetchInboxEmails(). Local state (read/unread, stars,
 * folders, queue statuses) is overlaid on top of whatever the API returns
 * and persisted in localStorage.
 *
 * Every action callback writes to localStorage (immediate cache) AND fires a
 * non-blocking API call to persist the state durably in the backend.
 */
import { useState, useCallback, useMemo, useEffect } from 'react';
import { fetchInboxEmails } from '../../../utils/api';
import { apiFetch } from '../../../services/apiClient';
import { useAuth } from '../../../contexts/AuthContext';
import { useConsumerUnit } from '../../../contexts/ConsumerUnitContext';

const STORAGE_KEY = 'nexonurse_inbox_state';

function loadPersistedState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function persistState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      readIds: [...state.readIds],
      starredIds: [...state.starredIds],
      folders: state.folders,
      queueStatuses: state.queueStatuses,
      boardSwimlanes: state.boardSwimlanes,
      scheduledDates: state.scheduledDates,
      reclassifiedDomains: state.reclassifiedDomains,
    }));
  } catch { /* localStorage full — degrade silently */ }
}

function initState() {
  const saved = loadPersistedState();
  const readIds = new Set(saved?.readIds ?? []);
  const starredIds = new Set(saved?.starredIds ?? []);
  const folders = saved?.folders ?? {};
  const queueStatuses = saved?.queueStatuses ?? {};
  const boardSwimlanes = saved?.boardSwimlanes ?? {};
  const scheduledDates = saved?.scheduledDates ?? {};
  const reclassifiedDomains = saved?.reclassifiedDomains ?? {};
  return { readIds, starredIds, folders, queueStatuses, boardSwimlanes, scheduledDates, reclassifiedDomains };
}

export function useInboxState() {
  const { getToken } = useAuth();
  const { selectedUnitId } = useConsumerUnit();
  const [state, setState] = useState(initState);
  const [baseEmails, setBaseEmails] = useState([]);

  // Authenticated wrapper — every backend call needs the JWT token
  const authFetch = useCallback((path, opts) => apiFetch(path, opts, getToken), [getToken]);

  // Fetch emails from API on mount.
  // When API emails arrive with persisted email_state, seed the local state
  // maps from those values instead of relying solely on localStorage.
  useEffect(() => {
    let cancelled = false;
    fetchInboxEmails(selectedUnitId, getToken).then((data) => {
      if (cancelled) return;
      if (Array.isArray(data) && data.length > 0) {
        setBaseEmails(data);
        // Seed local state from API-returned email_state fields
        setState((prev) => {
          const readIds = new Set(prev.readIds);
          const starredIds = new Set(prev.starredIds);
          const folders = { ...prev.folders };
          const queueStatuses = { ...prev.queueStatuses };
          const boardSwimlanes = { ...prev.boardSwimlanes };
          const scheduledDates = { ...prev.scheduledDates };
          const reclassifiedDomains = { ...prev.reclassifiedDomains };
          for (const e of data) {
            if (e.isRead) readIds.add(e.id); else readIds.delete(e.id);
            if (e.isStarred) starredIds.add(e.id); else starredIds.delete(e.id);
            if (e.folder) folders[e.id] = e.folder;
            if (e.queueStatus != null) queueStatuses[e.id] = e.queueStatus;
            if (e.boardSwimlane != null) boardSwimlanes[e.id] = e.boardSwimlane;
            if (e.scheduledDate != null) scheduledDates[e.id] = e.scheduledDate;
            if (e.reclassifiedDomain) reclassifiedDomains[e.id] = e.reclassifiedDomain;
            if (e.assignedTo) reclassifiedDomains[e.id] = prev.reclassifiedDomains[e.id] ?? reclassifiedDomains[e.id];
          }
          const next = { readIds, starredIds, folders, queueStatuses, boardSwimlanes, scheduledDates, reclassifiedDomains };
          persistState(next);
          return next;
        });
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [getToken, selectedUnitId]);

  const update = useCallback((updater) => {
    setState((prev) => {
      const next = updater(prev);
      persistState(next);
      return next;
    });
  }, []);

  // Derive enriched email list from baseEmails (API or mock) + local overrides
  const emails = useMemo(() =>
    baseEmails.map((e) => ({
      ...e,
      isRead: state.readIds.has(e.id),
      isStarred: state.starredIds.has(e.id),
      folder: state.folders[e.id] ?? e.folder,
      queueStatus: state.queueStatuses[e.id] ?? e.queueStatus,
      boardSwimlane: state.boardSwimlanes[e.id] ?? e.boardSwimlane,
      scheduledDate: state.scheduledDates[e.id] ?? e.scheduledDate,
      primaryDomain: state.reclassifiedDomains[e.id] ?? e.primaryDomain,
    })),
    [state, baseEmails]
  );

  // ── Actions ───────────────────────────────────────────────────────────────

  const markRead = useCallback((id) => {
    update((s) => {
      const next = new Set(s.readIds);
      next.add(id);
      return { ...s, readIds: next };
    });
    authFetch(`/api/emails/${id}`, { method: 'PUT', body: JSON.stringify({ isRead: true }) }).catch(() => {});
  }, [update, authFetch]);

  const markUnread = useCallback((id) => {
    update((s) => {
      const next = new Set(s.readIds);
      next.delete(id);
      return { ...s, readIds: next };
    });
    authFetch(`/api/emails/${id}`, { method: 'PUT', body: JSON.stringify({ isRead: false }) }).catch(() => {});
  }, [update, authFetch]);

  const toggleStar = useCallback((id) => {
    let isStarred;
    update((s) => {
      const next = new Set(s.starredIds);
      if (next.has(id)) { next.delete(id); isStarred = false; }
      else { next.add(id); isStarred = true; }
      return { ...s, starredIds: next };
    });
    // isStarred is set synchronously inside the updater above before the API fires
    authFetch(`/api/emails/${id}`, { method: 'PUT', body: JSON.stringify({ isStarred }) }).catch(() => {});
  }, [update, authFetch]);

  const moveToFolder = useCallback((id, folder) => {
    update((s) => ({ ...s, folders: { ...s.folders, [id]: folder } }));
    authFetch(`/api/emails/${id}`, { method: 'PUT', body: JSON.stringify({ folder }) }).catch(() => {});
  }, [update, authFetch]);

  const archiveEmail = useCallback((id) => moveToFolder(id, 'archive'), [moveToFolder]);
  const trashEmail = useCallback((id) => moveToFolder(id, 'trash'), [moveToFolder]);

  const queueEmail = useCallback((id) => {
    update((s) => ({
      ...s,
      queueStatuses: { ...s.queueStatuses, [id]: 'queued' },
      boardSwimlanes: { ...s.boardSwimlanes, [id]: 'today' },
      folders: { ...s.folders, [id]: 'queued' },
    }));
    authFetch(`/api/emails/${id}`, { method: 'PUT', body: JSON.stringify({ queueStatus: 'queued', boardSwimlane: 'today', folder: 'queued' }) }).catch(() => {});
    // Also create a task-based queue item so unified /api/queue views show it
    const email = baseEmails.find((e) => e.id === id);
    authFetch('/api/queue', { method: 'POST', body: JSON.stringify({
      title: (email?.subject || '').replace(/^(RE:|FWD:)\s*/gi, '') || 'Email task',
      category: email?.primaryDomain || 'operations',
      priority: email?.urgency >= 4 ? 'high' : email?.urgency >= 3 ? 'medium' : 'low',
      type: 'email_topic',
      dueDate: new Date().toLocaleDateString('en-CA'),
      threadContext: {
        sourceType: 'email_topic',
        threadId: email?.threadId,
        topicName: (email?.subject || '').replace(/^(RE:|FWD:)\s*/gi, ''),
        domain: email?.primaryDomain,
        urgency: email?.urgency,
        participants: [email?.from?.name || email?.from],
        messageCount: 1,
        attachmentCount: email?.attachments?.length || 0,
        latestPreview: email?.preview,
        emails: [{
          id: email?.id, from: email?.from?.name || email?.from, receivedAt: email?.receivedAt,
          subject: email?.subject, preview: email?.preview, bodyText: email?.bodyText || email?.preview,
          attachments: (email?.attachments || []).map(a => ({ name: a.name, size: a.size })),
        }],
      },
    })}).then(() => window.dispatchEvent(new Event('nexonurse:queue-changed'))).catch(() => {});
  }, [update, authFetch, baseEmails]);

  const scheduleEmail = useCallback((id, date) => {
    const email = baseEmails.find((e) => e.id === id);
    update((s) => ({
      ...s,
      queueStatuses: { ...s.queueStatuses, [id]: 'scheduled' },
      scheduledDates: { ...s.scheduledDates, [id]: date },
      boardSwimlanes: { ...s.boardSwimlanes, [id]: 'scheduled' },
      folders: { ...s.folders, [id]: 'queued' },
    }));
    authFetch(`/api/emails/${id}`, { method: 'PUT', body: JSON.stringify({ queueStatus: 'scheduled', scheduledDate: date, boardSwimlane: 'scheduled', folder: 'queued' }) }).catch(() => {});
    authFetch('/api/queue', { method: 'POST', body: JSON.stringify({
      title: (email?.subject || '').replace(/^(RE:|FWD:)\s*/gi, '') || 'Email task',
      category: email?.primaryDomain || 'operations',
      priority: email?.urgency >= 4 ? 'high' : email?.urgency >= 3 ? 'medium' : 'low',
      type: 'email_topic',
      dueDate: date,
      threadContext: {
        sourceType: 'email_topic', threadId: email?.threadId,
        topicName: (email?.subject || '').replace(/^(RE:|FWD:)\s*/gi, ''),
        domain: email?.primaryDomain, urgency: email?.urgency,
        participants: [email?.from?.name || email?.from], messageCount: 1,
        attachmentCount: email?.attachments?.length || 0, latestPreview: email?.preview,
        emails: [{ id: email?.id, from: email?.from?.name || email?.from, receivedAt: email?.receivedAt, subject: email?.subject, preview: email?.preview, bodyText: email?.bodyText || email?.preview, attachments: (email?.attachments || []).map(a => ({ name: a.name, size: a.size })) }],
      },
    })}).then(() => window.dispatchEvent(new Event('nexonurse:queue-changed'))).catch(() => {});
  }, [update, authFetch, baseEmails]);

  const boardEmail = useCallback((id, swimlane) => {
    const email = baseEmails.find((e) => e.id === id);
    update((s) => ({
      ...s,
      queueStatuses: { ...s.queueStatuses, [id]: 'queued' },
      boardSwimlanes: { ...s.boardSwimlanes, [id]: swimlane },
      scheduledDates: { ...s.scheduledDates, [id]: swimlane === 'scheduled' ? s.scheduledDates[id] : null },
      folders: { ...s.folders, [id]: 'queued' },
    }));
    authFetch(`/api/emails/${id}`, { method: 'PUT', body: JSON.stringify({
      queueStatus: 'queued', boardSwimlane: swimlane, folder: 'queued',
      ...(swimlane !== 'scheduled' ? { scheduledDate: null } : {}),
    }) }).catch(() => {});
    // Create queue item — backend generates UUID; bucket set via response id
    authFetch('/api/queue', { method: 'POST', body: JSON.stringify({
      title: (email?.subject || '').replace(/^(RE:|FWD:)\s*/gi, '') || 'Email task',
      category: email?.primaryDomain || 'operations',
      priority: email?.urgency >= 4 ? 'high' : email?.urgency >= 3 ? 'medium' : 'low',
      type: 'email_topic',
      bucket: swimlane,
      dueDate: swimlane === 'scheduled' ? email?.scheduledDate : new Date().toLocaleDateString('en-CA'),
      threadContext: {
        sourceType: 'email_topic',
        threadId: email?.threadId,
        topicName: (email?.subject || '').replace(/^(RE:|FWD:)\s*/gi, ''),
        domain: email?.primaryDomain,
        urgency: email?.urgency,
        participants: [email?.from?.name || email?.from],
        messageCount: 1,
        attachmentCount: email?.attachments?.length || 0,
        latestPreview: email?.preview,
        emails: [{
          id: email?.id, from: email?.from?.name || email?.from, receivedAt: email?.receivedAt,
          subject: email?.subject, preview: email?.preview, bodyText: email?.bodyText || email?.preview,
          attachments: (email?.attachments || []).map(a => ({ name: a.name, size: a.size })),
        }],
      },
    })}).then((res) => res.json()).then((created) => {
      // After creation, set the bucket using the server-generated UUID
      const bucketDueDate = swimlane === 'parking' ? null : new Date().toLocaleDateString('en-CA');
      authFetch(`/api/queue/${created.id}/bucket`, { method: 'PUT', body: JSON.stringify({ bucket: swimlane, dueDate: bucketDueDate }) })
        .then(() => window.dispatchEvent(new Event('nexonurse:queue-changed'))).catch(() => {});
    }).catch(() => {});
  }, [update, authFetch, baseEmails]);

  const dismissEmail = useCallback((id) => {
    update((s) => ({
      ...s,
      folders: { ...s.folders, [id]: 'archive' },
      queueStatuses: { ...s.queueStatuses, [id]: 'none' },
    }));
    authFetch(`/api/emails/${id}`, { method: 'PUT', body: JSON.stringify({ folder: 'archive', queueStatus: 'none' }) }).catch(() => {});
  }, [update, authFetch]);

  const completeEmail = useCallback((id) => {
    update((s) => ({
      ...s,
      queueStatuses: { ...s.queueStatuses, [id]: 'completed' },
      boardSwimlanes: { ...s.boardSwimlanes, [id]: null },
    }));
    authFetch(`/api/emails/${id}`, { method: 'PUT', body: JSON.stringify({ queueStatus: 'completed', boardSwimlane: null }) }).catch(() => {});
  }, [update, authFetch]);

  const reclassify = useCallback((id, domain) => {
    update((s) => ({
      ...s,
      reclassifiedDomains: { ...s.reclassifiedDomains, [id]: domain },
    }));
    authFetch(`/api/emails/${id}`, { method: 'PUT', body: JSON.stringify({ reclassifiedDomain: domain }) }).catch(() => {});
  }, [update, authFetch]);

  // ── Topic-group-level actions (all emails sharing a subcategory, or threadId fallback) ──

  /** Resolve all email IDs that share a threadId with the given email */
  const getThreadIds = useCallback((id) => {
    const email = baseEmails.find((e) => e.id === id);
    if (!email?.threadId) return [id];
    return baseEmails.filter((e) => e.threadId === email.threadId).map((e) => e.id);
  }, [baseEmails]);

  /** Resolve all email IDs in the same topic group (by subcategory within domain, falling back to threadId) */
  const getTopicGroupIds = useCallback((id) => {
    const email = baseEmails.find((e) => e.id === id);
    if (!email) return [id];
    if (email.subcategory) {
      return baseEmails
        .filter((e) => e.subcategory === email.subcategory && e.primaryDomain === email.primaryDomain)
        .map((e) => e.id);
    }
    return getThreadIds(id);
  }, [baseEmails, getThreadIds]);

  /** Build threadContext payload for a group of emails */
  const buildThreadContext = (groupEmails, latest, extraFields = {}) => ({
    sourceType: 'email_topic',
    subcategory: latest?.subcategory || null,
    threadId: latest?.threadId,
    topicName: (latest?.subject || '').replace(/^(RE:|FWD:)\s*/gi, ''),
    domain: latest?.primaryDomain,
    urgency: latest?.urgency,
    participants: [...new Set(groupEmails.map(e => e.from?.name || e.from))],
    messageCount: groupEmails.length,
    attachmentCount: groupEmails.reduce((n, e) => n + (e.attachments?.length || 0), 0),
    latestPreview: latest?.preview,
    threadSummary: latest?.threadSummary,
    emails: groupEmails.map(e => ({
      id: e.id, from: e.from?.name || e.from, receivedAt: e.receivedAt,
      subject: e.subject, preview: e.preview, bodyText: e.bodyText || e.preview,
      attachments: (e.attachments || []).map(a => ({ name: a.name, size: a.size })),
    })),
    ...extraFields,
  });

  const queueThread = useCallback((id) => {
    const ids = getTopicGroupIds(id);
    const groupEmails = baseEmails.filter((e) => ids.includes(e.id));
    const latest = groupEmails[0];
    const subject = (latest?.subject || '').replace(/^(RE:|FWD:)\s*/gi, '');

    update((s) => {
      const qs = { ...s.queueStatuses };
      const bs = { ...s.boardSwimlanes };
      const folders = { ...s.folders };
      ids.forEach((eid) => { qs[eid] = 'queued'; bs[eid] = 'today'; folders[eid] = 'queued'; });
      return { ...s, queueStatuses: qs, boardSwimlanes: bs, folders };
    });
    ids.forEach((eid) => authFetch(`/api/emails/${eid}`, {
      method: 'PUT', body: JSON.stringify({ queueStatus: 'queued', boardSwimlane: 'today', folder: 'queued' })
    }).catch(() => {}));

    authFetch('/api/queue', { method: 'POST', body: JSON.stringify({
      title: subject || 'Email topic',
      category: latest?.primaryDomain || 'operations',
      priority: latest?.urgency >= 4 ? 'high' : latest?.urgency >= 3 ? 'medium' : 'low',
      type: 'email_topic',
      dueDate: new Date().toLocaleDateString('en-CA'),
      threadContext: buildThreadContext(groupEmails, latest),
    })}).then(() => window.dispatchEvent(new Event('nexonurse:queue-changed'))).catch(() => {});
  }, [update, authFetch, baseEmails, getTopicGroupIds]);

  const scheduleThread = useCallback((id, date) => {
    const ids = getTopicGroupIds(id);
    const groupEmails = baseEmails.filter((e) => ids.includes(e.id));
    const latest = groupEmails[0];
    const subject = (latest?.subject || '').replace(/^(RE:|FWD:)\s*/gi, '');

    update((s) => {
      const qs = { ...s.queueStatuses };
      const sd = { ...s.scheduledDates };
      const bs = { ...s.boardSwimlanes };
      const folders = { ...s.folders };
      ids.forEach((eid) => { qs[eid] = 'scheduled'; sd[eid] = date; bs[eid] = 'scheduled'; folders[eid] = 'queued'; });
      return { ...s, queueStatuses: qs, scheduledDates: sd, boardSwimlanes: bs, folders };
    });
    ids.forEach((eid) => authFetch(`/api/emails/${eid}`, {
      method: 'PUT', body: JSON.stringify({ queueStatus: 'scheduled', scheduledDate: date, boardSwimlane: 'scheduled', folder: 'queued' })
    }).catch(() => {}));

    authFetch('/api/queue', { method: 'POST', body: JSON.stringify({
      title: subject || 'Email topic',
      category: latest?.primaryDomain || 'operations',
      priority: latest?.urgency >= 4 ? 'high' : latest?.urgency >= 3 ? 'medium' : 'low',
      type: 'email_topic',
      dueDate: date,
      threadContext: buildThreadContext(groupEmails, latest),
    })}).then(() => window.dispatchEvent(new Event('nexonurse:queue-changed'))).catch(() => {});
  }, [update, authFetch, baseEmails, getTopicGroupIds]);

  const boardThread = useCallback((id, swimlane) => {
    const ids = getTopicGroupIds(id);
    const groupEmails = baseEmails.filter((e) => ids.includes(e.id));
    const latest = groupEmails[0];
    const subject = (latest?.subject || '').replace(/^(RE:|FWD:)\s*/gi, '');

    update((s) => {
      const qs = { ...s.queueStatuses };
      const bs = { ...s.boardSwimlanes };
      const sd = { ...s.scheduledDates };
      const folders = { ...s.folders };
      ids.forEach((eid) => {
        qs[eid] = 'queued';
        bs[eid] = swimlane;
        if (swimlane !== 'scheduled') sd[eid] = null;
        folders[eid] = 'queued';
      });
      return { ...s, queueStatuses: qs, boardSwimlanes: bs, scheduledDates: sd, folders };
    });
    ids.forEach((eid) => authFetch(`/api/emails/${eid}`, { method: 'PUT', body: JSON.stringify({
      queueStatus: 'queued', boardSwimlane: swimlane, folder: 'queued',
      ...(swimlane !== 'scheduled' ? { scheduledDate: null } : {}),
    }) }).catch(() => {}));
    authFetch('/api/queue', { method: 'POST', body: JSON.stringify({
      title: subject || 'Email topic',
      category: latest?.primaryDomain || 'operations',
      priority: latest?.urgency >= 4 ? 'high' : latest?.urgency >= 3 ? 'medium' : 'low',
      type: 'email_topic',
      bucket: swimlane,
      dueDate: swimlane === 'parking' ? null : new Date().toLocaleDateString('en-CA'),
      threadContext: buildThreadContext(groupEmails, latest),
    })}).then((res) => res.json()).then((created) => {
      const bucketDueDate = swimlane === 'parking' ? null : new Date().toLocaleDateString('en-CA');
      authFetch(`/api/queue/${created.id}/bucket`, { method: 'PUT', body: JSON.stringify({ bucket: swimlane, dueDate: bucketDueDate }) })
        .then(() => window.dispatchEvent(new Event('nexonurse:queue-changed'))).catch(() => {});
    }).catch(() => {});
  }, [update, authFetch, baseEmails, getTopicGroupIds]);

  const dismissThread = useCallback((id) => {
    const ids = getTopicGroupIds(id);
    update((s) => {
      const f = { ...s.folders };
      const qs = { ...s.queueStatuses };
      ids.forEach((eid) => { f[eid] = 'archive'; qs[eid] = 'none'; });
      return { ...s, folders: f, queueStatuses: qs };
    });
    ids.forEach((eid) => authFetch(`/api/emails/${eid}`, { method: 'PUT', body: JSON.stringify({ folder: 'archive', queueStatus: 'none' }) }).catch(() => {}));
  }, [update, authFetch, getTopicGroupIds]);

  // ── Bulk actions ──────────────────────────────────────────────────────────

  const bulkMarkRead = useCallback((ids) => {
    update((s) => {
      const next = new Set(s.readIds);
      ids.forEach((id) => next.add(id));
      return { ...s, readIds: next };
    });
  }, [update]);

  const bulkArchive = useCallback((ids) => {
    update((s) => {
      const nextFolders = { ...s.folders };
      ids.forEach((id) => { nextFolders[id] = 'archive'; });
      return { ...s, folders: nextFolders };
    });
  }, [update]);

  const bulkTrash = useCallback((ids) => {
    update((s) => {
      const nextFolders = { ...s.folders };
      ids.forEach((id) => { nextFolders[id] = 'trash'; });
      return { ...s, folders: nextFolders };
    });
  }, [update]);

  const bulkMoveToInbox = useCallback((ids) => {
    update((s) => {
      const nextFolders = { ...s.folders };
      ids.forEach((id) => { nextFolders[id] = 'inbox'; });
      return { ...s, folders: nextFolders };
    });
  }, [update]);

  const bulkMarkUnread = useCallback((ids) => {
    update((s) => {
      const next = new Set(s.readIds);
      ids.forEach((id) => next.delete(id));
      return { ...s, readIds: next };
    });
  }, [update]);

  const resetState = useCallback(() => {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    setState(initState());
  }, []);

  // ── Folder counts ─────────────────────────────────────────────────────────

  const folderCounts = useMemo(() => {
    const counts = { inbox: 0, sent: 0, drafts: 0, archive: 0, trash: 0 };
    const unread = { inbox: 0, sent: 0, drafts: 0, archive: 0, trash: 0 };
    emails.forEach((e) => {
      const f = e.folder;
      // Triaged inbox emails don't count toward inbox totals
      if (f === 'inbox' && e.queueStatus !== 'none') return;
      if (counts[f] !== undefined) counts[f]++;
      if (!e.isRead && unread[f] !== undefined) unread[f]++;
    });
    return { total: counts, unread };
  }, [emails]);

  return {
    emails,
    folderCounts,
    actions: {
      markRead, markUnread, toggleStar,
      moveToFolder, archiveEmail, trashEmail,
      queueEmail, scheduleEmail, boardEmail, dismissEmail, completeEmail,
      queueThread, scheduleThread, boardThread, dismissThread,
      reclassify,
      bulkMarkRead, bulkMarkUnread, bulkArchive, bulkTrash, bulkMoveToInbox,
      resetState,
    },
  };
}
